import { Clock, MapPin, Sparkles, Users, type LucideIcon } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import type { FirestoreSemanticData } from "reedy-data/models";
import { Text } from "~/components/text";
import { Badge } from "~/components/ui/badge";

type WorldSettings = NonNullable<FirestoreSemanticData["worldSettings"]>;
type Setting = NonNullable<WorldSettings["settings"]>[number];

interface WorldSettingSectionProps {
  contentLanguage: string | undefined;
  worldSettings: WorldSettings;
}

interface DetailRowProps {
  contentLanguage: string | undefined;
  icon: LucideIcon;
  label: string;
  value: string | null | undefined;
}

const settingTypeOrder = ["primary", "major", "minor"];

const DetailRow = ({
  contentLanguage,
  icon: RowIcon,
  label,
  value,
}: DetailRowProps) => (
  <div className="grid gap-3 px-5 py-4 sm:grid-cols-[180px_minmax(0,1fr)] sm:gap-0 sm:py-0">
    <dt className="flex items-start gap-2.5 sm:border-e sm:border-secondary-200 sm:py-4 sm:pe-6">
      <RowIcon
        aria-hidden="true"
        className="mt-0.5 size-4 shrink-0 text-secondary-500"
      />
      <Text as="span" variant="label">
        {label}
      </Text>
    </dt>
    <Text
      as="dd"
      variant="body"
      className="text-pretty sm:py-4 sm:ps-6"
      lang={contentLanguage}
    >
      {value}
    </Text>
  </div>
);

const SettingGroup = ({
  contentLanguage,
  setting,
}: {
  contentLanguage: string | undefined;
  setting: Setting;
}) => {
  const { t } = useTranslation(["productDetails"]);
  const badgeId = useId();
  const settingLabelId = useId();
  const settingLabel = [setting.locationLabel, setting.timePeriodLabel]
    .filter(Boolean)
    .join(" · ");
  const settingTypeLabel = setting.settingType
    ? `${setting.settingType[0].toUpperCase()}${setting.settingType.slice(1)}`
    : undefined;

  return (
    <section
      aria-labelledby={settingLabel ? `${badgeId} ${settingLabelId}` : badgeId}
      className="overflow-hidden rounded-xl border border-secondary-200 bg-white"
    >
      <div className="flex flex-wrap items-center gap-2.5 border-b border-secondary-200 bg-secondary-25 px-5 py-3">
        <Badge
          id={badgeId}
          variant={setting.settingType === "primary" ? "default" : "secondary"}
        >
          {settingTypeLabel}
        </Badge>
        {settingLabel && (
          <div
            id={settingLabelId}
            className="flex min-w-0 items-center"
            lang={contentLanguage}
          >
            {setting.locationLabel && (
              <Text as="span" variant="bodyStrong">
                {setting.locationLabel}
              </Text>
            )}
            {setting.locationLabel && setting.timePeriodLabel && (
              <>
                {" "}
                <Text as="span" variant="muted" className="px-3">
                  ·
                </Text>{" "}
              </>
            )}
            {setting.timePeriodLabel && (
              <Text as="span" variant="muted">
                {setting.timePeriodLabel}
              </Text>
            )}
          </div>
        )}
      </div>
      <dl className="divide-y divide-secondary-200">
        <DetailRow
          contentLanguage={contentLanguage}
          icon={MapPin}
          label={t("productDetails:summary.worldSetting.location")}
          value={setting.location}
        />
        <DetailRow
          contentLanguage={contentLanguage}
          icon={Clock}
          label={t("productDetails:summary.worldSetting.timePeriod")}
          value={setting.timePeriod}
        />
        <DetailRow
          contentLanguage={contentLanguage}
          icon={Users}
          label={t("productDetails:summary.worldSetting.socialStructure")}
          value={setting.localSocialStructure}
        />
      </dl>
    </section>
  );
};

export const WorldSettingSection = ({
  contentLanguage,
  worldSettings,
}: WorldSettingSectionProps) => {
  const { t } = useTranslation(["productDetails"]);
  const settings = worldSettings.settings ?? [];
  const orderedSettings = [
    ...settingTypeOrder.flatMap((type) =>
      settings.filter((setting) => setting.settingType === type),
    ),
    ...settings.filter(
      (setting) => !settingTypeOrder.includes(setting.settingType ?? ""),
    ),
  ];
  const settingOccurrences = new Map<string, number>();
  const keyedSettings = orderedSettings.map((setting) => {
    const signature = [
      setting.settingType,
      setting.locationLabel,
      setting.timePeriodLabel,
      setting.location,
      setting.timePeriod,
      setting.localSocialStructure,
    ].join("|");
    const occurrence = settingOccurrences.get(signature) ?? 0;
    settingOccurrences.set(signature, occurrence + 1);
    return { key: `${signature}-${occurrence}`, setting };
  });

  return (
    <section
      aria-labelledby="world-setting-title"
      className="flex flex-col gap-6"
    >
      <Text
        id="world-setting-title"
        as="h2"
        variant="heading"
        className="text-balance"
      >
        {t("productDetails:summary.sections.worldSetting")}
      </Text>

      <section
        aria-labelledby="world-setting-global"
        className="overflow-hidden rounded-xl border border-secondary-200 bg-white"
      >
        <div className="flex items-center border-b border-secondary-200 bg-secondary-25 px-5 py-3">
          <Badge id="world-setting-global">
            {t("productDetails:summary.worldSetting.global")}
          </Badge>
        </div>
        <dl className="divide-y divide-secondary-200">
          <DetailRow
            contentLanguage={contentLanguage}
            icon={Users}
            label={t("productDetails:summary.worldSetting.socialStructure")}
            value={worldSettings.socialStructure}
          />
          <DetailRow
            contentLanguage={contentLanguage}
            icon={Sparkles}
            label={t("productDetails:summary.worldSetting.metaphysics")}
            value={worldSettings.metaphysics}
          />
        </dl>
      </section>

      {keyedSettings.map(({ key, setting }) => (
        <SettingGroup
          key={key}
          contentLanguage={contentLanguage}
          setting={setting}
        />
      ))}
    </section>
  );
};
